<template>
  <b-card :title="game ? 'Конфигурация игры' : 'Создать игру'">
    <b-overlay
      :show="isLoading"
      rounded="sm"
    >
      <b-form @submit.prevent="onSubmit">
        <b-row>
          <b-col md="5">
            <b-form-group
              label="Название игры"
              label-for="builder-name"
            >
              <b-form-input
                id="builder-name"
                v-model="form.name"
                placeholder="Название"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group
              label="Тип"
              label-for="builder-type"
            >
              <v-select
                id="builder-type"
                v-model="form.type"
                :options="typeOptions"
                :reduce="val => val.value"
                :clearable="false"
                :disabled="!!game"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Шкала стоимости пакета">
              <b-form-radio-group
                v-model="form.priceScale"
                :options="packScaleOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          label="Количество раундов (без финала)"
          label-for="builder-rounds-count"
        >
          <b-form-spinbutton
            id="builder-rounds-count"
            :value="form.rounds.length"
            min="1"
            max="10"
            style="max-width: 160px"
            @input="onRoundsCountChange"
          />
        </b-form-group>

        <b-card
          v-for="(round, roundIndex) in form.rounds"
          :key="`round-${roundIndex}`"
          border-variant="secondary"
          class="mb-1"
          body-class="p-1"
        >
          <div class="d-flex align-items-center justify-content-between mb-1">
            <h5 class="mb-0">
              Раунд {{ roundIndex + 1 }}
              <small class="text-muted">(база {{ basePrices[roundIndex] }})</small>
            </h5>
            <b-form-radio-group
              :checked="round.type"
              :options="roundTypeOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
              @input="value => onRoundTypeChange(roundIndex, value)"
            />
          </div>

          <b-row>
            <b-col md="3">
              <b-form-group label="Тем в раунде">
                <b-form-spinbutton
                  :value="round.topics.length"
                  min="1"
                  max="10"
                  @input="value => onTopicsCountChange(roundIndex, value)"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Вопросов в темах">
                <b-form-spinbutton
                  v-model="round.questionsPerTopic"
                  min="1"
                  max="10"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <div
            v-for="(topic, topicIndex) in round.topics"
            :key="`topic-${roundIndex}-${topicIndex}`"
            class="d-flex align-items-center mb-50"
            style="gap: 12px"
          >
            <span style="min-width: 160px">
              Тема {{ topicIndex + 1 }}<template v-if="topic.name"> — {{ topic.name }}</template>
            </span>
            <b-form-radio-group
              :checked="topic.priceScale"
              :options="topicScaleOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
              @input="value => onTopicScaleChange(roundIndex, topicIndex, value)"
            />
            <v-select
              v-if="form.type === 'music'"
              :value="topic.questionFormat"
              :options="questionFormatOptions"
              :reduce="option => option.value"
              :clearable="false"
              label="label"
              style="min-width: 170px"
              @input="value => { topic.questionFormat = value }"
            />
            <b-form-input
              v-if="topic.priceScale === 'flat'"
              :value="topic.flatPrice"
              type="number"
              min="1"
              style="max-width: 110px"
              @input="value => onFlatPriceInput(roundIndex, topicIndex, value)"
            />
            <b-button
              variant="flat-danger"
              size="sm"
              :disabled="round.topics.length <= 1"
              title="Удалить тему"
              @click="removeTopic(roundIndex, topicIndex)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </b-card>

        <b-form-group
          label="Финальных тем"
          label-for="builder-final-topics"
        >
          <b-form-spinbutton
            id="builder-final-topics"
            v-model="form.finalTopicsCount"
            min="1"
            max="10"
            style="max-width: 160px"
          />
        </b-form-group>

        <h5 class="mt-2">
          Превью структуры
        </h5>
        <b-table-simple
          small
          bordered
          responsive
          class="mb-2"
        >
          <b-tbody>
            <template v-for="(group, groupIndex) in preview">
              <b-tr
                :key="`preview-header-${groupIndex}`"
                variant="secondary"
              >
                <b-th :colspan="group.columnsCount + 1">
                  {{ group.label }}
                </b-th>
              </b-tr>
              <b-tr
                v-for="(row, rowIndex) in group.rows"
                :key="`preview-${groupIndex}-${rowIndex}`"
              >
                <b-td class="text-nowrap">
                  {{ row.label }}
                </b-td>
                <b-td
                  v-for="(price, priceIndex) in row.prices"
                  :key="priceIndex"
                  class="text-center"
                >
                  {{ price }}
                </b-td>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>

        <b-button
          variant="primary"
          type="submit"
          :disabled="isLoading || !form.name"
        >
          <span v-if="isLoading">
            <b-spinner small />
            Сохранение...
          </span>
          <span v-else>
            {{ game ? 'Сохранить конфигурацию' : 'Создать' }}
          </span>
        </b-button>
      </b-form>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard, BForm, BFormGroup, BFormInput, BFormRadioGroup, BFormSpinbutton,
  BButton, BOverlay, BSpinner, BRow, BCol,
  BTableSimple, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { reactive, computed, watch } from '@vue/composition-api'
import { computeBasePrices, defaultFlatPrice, topicPrices } from './gameBuilderPricing'
import { QUESTION_FORMAT_OPTIONS, DEFAULT_QUESTION_FORMAT } from './gameBuilderMusicFormats'

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BFormSpinbutton,
    BButton,
    BOverlay,
    BSpinner,
    BRow,
    BCol,
    BTableSimple,
    BTbody,
    BTr,
    BTh,
    BTd,
    vSelect,
  },
  props: {
    game: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const typeOptions = [
      { value: 'movies', label: 'Фильмы' },
      { value: 'series', label: 'Сериалы' },
      { value: 'music', label: 'Музыка' },
    ]
    const packScaleOptions = [
      { value: 'classic', text: 'Классическая' },
      { value: 'flat', text: 'Плоские раунды' },
    ]
    const roundTypeOptions = [
      { value: 'main', text: 'Основной' },
      { value: 'small', text: 'Малый' },
    ]
    const topicScaleOptions = [
      { value: 'classic', text: 'Классическая' },
      { value: 'flat', text: 'Плоская' },
    ]

    const makeTopic = (roundType, basePrice) => ({
      id: null,
      name: null,
      priceScale: roundType === 'small' ? 'flat' : 'classic',
      flatPrice: defaultFlatPrice(basePrice),
      flatPriceTouched: false,
      questionFormat: DEFAULT_QUESTION_FORMAT,
    })

    const makeRound = (type, basePrice) => ({
      id: null,
      type,
      questionsPerTopic: 5,
      topics: Array.from(
        { length: type === 'small' ? 1 : 6 },
        () => makeTopic(type, basePrice),
      ),
    })

    const buildDefaultForm = () => reactive({
      name: '',
      type: 'movies',
      priceScale: 'classic',
      rounds: [
        makeRound('main', 100),
        makeRound('main', 200),
        makeRound('main', 300),
      ],
      finalTopicsCount: 4,
    })

    const buildFormFromGame = gameData => {
      const regularRounds = gameData.rounds.filter(round => !round.is_final)
      const finalRound = gameData.rounds.find(round => round.is_final)

      return reactive({
        name: gameData.name || '',
        type: gameData.type === 'builder-series' ? 'series'
          : gameData.type === 'builder-music' ? 'music' : 'movies',
        priceScale: gameData.price_scale || 'classic',
        rounds: regularRounds.map(round => ({
          id: round.id,
          type: round.type || 'main',
          questionsPerTopic: round.questions_per_topic || 5,
          topics: round.topics.map(roundTopic => ({
            id: roundTopic.id,
            name: roundTopic.autotopic_title || roundTopic.topic_name || null,
            priceScale: roundTopic.price_scale || 'classic',
            flatPrice: roundTopic.flat_price || defaultFlatPrice(round.base_price || 100),
            flatPriceTouched: !!roundTopic.flat_price,
            questionFormat: roundTopic.question_format || DEFAULT_QUESTION_FORMAT,
          })),
        })),
        finalTopicsCount: finalRound ? finalRound.topics.length : 4,
      })
    }

    const form = props.game ? buildFormFromGame(props.game) : buildDefaultForm()

    const basePrices = computed(() => computeBasePrices(
      form.priceScale,
      [...form.rounds.map(round => round.type), 'final'],
    ))

    // Нетронутые плоские цены следуют за базой раунда
    watch(basePrices, () => {
      form.rounds.forEach((round, roundIndex) => {
        round.topics.forEach(topic => {
          if (topic.priceScale === 'flat' && !topic.flatPriceTouched) {
            topic.flatPrice = defaultFlatPrice(basePrices.value[roundIndex])
          }
        })
      })
    })

    const preview = computed(() => {
      const groups = form.rounds.map((round, roundIndex) => ({
        label: `Раунд ${roundIndex + 1}${round.type === 'small' ? ' (Малый)' : ''} — база ${basePrices.value[roundIndex]}`,
        columnsCount: round.questionsPerTopic,
        rows: round.topics.map((topic, topicIndex) => ({
          label: topic.name ? `Тема ${topicIndex + 1} — ${topic.name}` : `Тема ${topicIndex + 1}`,
          prices: topicPrices(topic, basePrices.value[roundIndex], round.questionsPerTopic),
        })),
      }))

      const finalBase = basePrices.value[form.rounds.length]
      groups.push({
        label: 'Финал',
        columnsCount: 1,
        rows: Array.from({ length: form.finalTopicsCount }, (unused, index) => ({
          label: `Тема ${index + 1}`,
          prices: [finalBase],
        })),
      })

      return groups
    })

    const onRoundsCountChange = value => {
      while (form.rounds.length < value) {
        form.rounds.push(makeRound('main', basePrices.value[form.rounds.length] || 100))
      }
      while (form.rounds.length > value) {
        form.rounds.pop()
      }
    }

    const onTopicsCountChange = (roundIndex, value) => {
      const round = form.rounds[roundIndex]
      while (round.topics.length < value) {
        round.topics.push(makeTopic(round.type, basePrices.value[roundIndex]))
      }
      while (round.topics.length > value) {
        round.topics.pop()
      }
    }

    const onRoundTypeChange = (roundIndex, value) => {
      const round = form.rounds[roundIndex]
      round.type = value
      // Сброс дефолтов: Основной — 6 классических тем, Малый — 1 плоская
      round.topics = Array.from(
        { length: value === 'small' ? 1 : 6 },
        () => makeTopic(value, basePrices.value[roundIndex]),
      )
    }

    const onTopicScaleChange = (roundIndex, topicIndex, value) => {
      const topic = form.rounds[roundIndex].topics[topicIndex]
      topic.priceScale = value
      if (value === 'flat' && !topic.flatPriceTouched) {
        topic.flatPrice = defaultFlatPrice(basePrices.value[roundIndex])
      }
    }

    const onFlatPriceInput = (roundIndex, topicIndex, value) => {
      const topic = form.rounds[roundIndex].topics[topicIndex]
      topic.flatPrice = value
      topic.flatPriceTouched = true
    }

    const removeTopic = (roundIndex, topicIndex) => {
      form.rounds[roundIndex].topics.splice(topicIndex, 1)
    }

    const onSubmit = () => {
      const payload = {
        name: form.name,
        type: form.type,
        price_scale: form.priceScale,
        rounds: form.rounds.map((round, roundIndex) => ({
          ...(round.id ? { id: round.id } : {}),
          type: round.type,
          questions_per_topic: round.questionsPerTopic,
          topics: round.topics.map(topic => ({
            ...(topic.id ? { id: topic.id } : {}),
            price_scale: topic.priceScale,
            ...(topic.priceScale === 'flat'
              ? { flat_price: Number(topic.flatPrice) || defaultFlatPrice(basePrices.value[roundIndex]) }
              : {}),
            ...(form.type === 'music' ? { question_format: topic.questionFormat } : {}),
          })),
        })),
        final_topics_count: form.finalTopicsCount,
      }

      emit('save', payload)
    }

    return {
      form,
      typeOptions,
      questionFormatOptions: QUESTION_FORMAT_OPTIONS,
      packScaleOptions,
      roundTypeOptions,
      topicScaleOptions,
      basePrices,
      preview,
      onRoundsCountChange,
      onTopicsCountChange,
      onRoundTypeChange,
      onTopicScaleChange,
      onFlatPriceInput,
      removeTopic,
      onSubmit,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
